/**
 * 69.4 step 5, convoys (BUILDLIST 69.4; DECISIONS 166, the owner: "a supply truck … not used as transit to the front to
 * battle, but only used on supply runs"; "just a pickup with a driver and passenger seat"; the rule the coordinator
 * approved, Oct 12), with GameConfig.convoys (needs fobs and vehicleStock): the FOB's supply becomes a thing that travels.
 * - **The truck**: each side fields CONVOY.trucks supply trucks (`supplyTruck`, a `cargoOnly` body: no ride, loop or marker
 *   takes it — core/body.ts) parked at its base; the vehicle stock (63.1n) replaces one destroyed, empty.
 * - **The base's stock** (`state.convoys.stock`): CONVOY.baseStock loads a side at the start, one more every
 *   CONVOY.baseRefillS up to it — the rear's output, generic supply (166: what it is made of stays open).
 * - **The cargo** (`stepConvoys`, on the Match's 1 s beat): a truck at its home (within CONVOY.loadReach: `atHome`) and not
 *   full takes on a full load (CONVOY.load, a FOB crate's worth) while the base's stock is above CONVOY.baseMinimum,
 *   spending one; a loaded truck within CONVOY.unloadReach of a FOB of its side tops that crate up to its stock — linked or
 *   cut: the cargo goes by road — and keeps what doesn't fit; a destroyed one loses its cargo with it. A dead driver
 *   leaves the cargo aboard where it stands.
 * - **The refill** (`linkRefills`): a side with supply trucks in its stock has its FOBs filled by its runs only — the
 *   link's trickle (core/fob.ts) stops; a side with none keeps it.
 * - **The run** (`planConvoys`, the commander's errand, on its re-plan beat): a FOB of the side below CONVOY.callBelow of
 *   its stock with no run bound for it gets the nearest loaded, idle truck with a free bot (alive, a bot nobody holds, on
 *   foot, no task, no human's order) within CONVOY.squadReach of it, and that bot alone a ride task there (`convoy` leg
 *   'out'; the ride's own brain drives it, ai/rideBrain.ts); arrived, once the FOB can take nothing more of it (or is
 *   gone), the home leg ('back'), which lapses as the truck stands home again. Each leg expires after CONVOY.legSeconds.
 * - **The driver from the base** (`stepConvoys`' calls): a run wanted — the FOB low, a loaded truck idle at home — with no
 *   free bot within reach of the truck calls a driver: the side's next respawn wave puts its first dead bot (never a
 *   human's unit) beside the truck (core: `baseSpawnFor`; the event's `at: 'base'`), and the commander sends it.
 * - **Redeploy forward**: a driver home from its run doesn't walk to the front — the next respawn wave takes it off the
 *   field without a ticket and lands it at the side's forward place (69.4 step 3's arrival anchor under Operations, else
 *   its beacon; `redeployed: true` on the event); with none it stays at the base.
 * - **The standing crew** (69.42 (g), DECISIONS 268 (1)): a respawn wave seats a side's first dead bot at the wheel of its
 *   truck standing home with nobody at it (`truckCrewSeat`); it holds there (`truckCrewHolds`) and is the run's driver; home
 *   at the wheel again, its run ends 'stayed'. (h): a truck away with no run is driven home (`planConvoys`), to load there.
 * - **Ammunition** (69.30 step 6, with GameConfig.munitionsSupply): the truck's bed (`StructureState.ammo`, loaded at home
 *   from the side's base crates — core/munitions.ts `loadBeds`) rides with the run: a bed with rounds is a load to set out
 *   with, a FOB's crate short of its rounds (MUNITIONS.fob, by type) is low as its bandages are, and a truck in unload reach
 *   tops it up toward them; the home leg waits till the FOB can take nothing more of either. The salvo's light shells stay
 *   at the base (no bed takes them); nothing refills the base (248 (4)).
 * Every call and run is kept in `state.convoys` for the order log (core/orderLog.ts). Pure; nothing runs without the switch.
 */
import type { ConvoyCall, ConvoyRun, GameState, PlayerState, SquadOrder, StructureState, Vec3 } from '../types';
import { vecNormalize } from '../math';
import { CONVOY, FOB, PHYS_DT, BUILD_SUPPLY, BUILD_COSTS, MUNITIONS, FORWARD_DUMP } from '../constants';
import { bedOf, dumpHolds, rearmStores } from './munitions';
import { crateTeam } from './supply';
import { cargoOnly, drives, structureTeam } from './body';
import { squadTaskFor, squadOrderSourceFor } from './squads';
import { rideArrived } from './ride';
import { arcDistance } from './gameState';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { roadsFor } from './roads';
import { routeCost } from './planetRoutes';
import { takeStock } from './stockTake';   // (not core/supplyStock: it reads the motor pool, and that closed a cycle back to core/modules)

export type ConvoyOrder = Extract<SquadOrder, { kind: 'ride' }> & { convoy: { fobId: number; leg: 'out' | 'back' } };

export type ConvoyEvent =
  | { kind: 'convoyLoaded'; structureId: number; team: number }
  | { kind: 'convoyDelivered'; structureId: number; fobId: number; team: number; bandages: number; magazines: number; material?: number; ammo?: Partial<Record<FobAmmo, number>> }   // (6b: `material` with build costs; 69.30 step 6: `ammo`, the rounds by type)
  | { kind: 'convoyLost'; structureId: number; team: number; bandages: number; magazines: number };

/** Are convoys on in this match (they need FOBs — so the web — and trucks that come back: the vehicle stock)? */
export function convoysOn(config: Pick<GameState['config'], 'convoys' | 'fobs' | 'relays' | 'vehicleStock'>): boolean {
  return !!config.convoys && !!config.fobs && !!config.relays && (config.vehicleStock ?? 0) > 0;   // (core/fob.ts fobsOn, without its import: fob.ts reads this module)
}

/** Does the link still refill `team`'s FOBs? Yes unless convoys are on and the side has supply trucks in its stock. */
export function linkRefills(state: Pick<GameState, 'config' | 'vehicleStocks'>, team: number): boolean {
  return !convoysOn(state.config) || !state.vehicleStocks?.[team]?.supplyTruck;
}

/** The setup's base stocks (CONVOY.baseStock a side) and empty logs. */
export function withConvoys(state: GameState): GameState {
  if (!convoysOn(state.config)) return state;
  const stock: Record<number, number> = {};
  for (const t of [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b)) stock[t] = CONVOY.baseStock;
  // 69.4 step 6b (with buildCosts): the base's material, its stock of loads' worth (core/buildCosts.ts)
  // (the relays and beacons the setup issued are paid from it: a carried kind pays when drawn into the carry — 6's bot half)
  const issued = (t: number) => (state.squads ?? []).filter(q => q.team === t).reduce((n, q) => n + (q.relays ?? 0) * BUILD_COSTS.relay, 0)
    + state.players.filter(p => p.teamIndex === t).reduce((n, p) => n + (p.beacons ?? 0) * BUILD_COSTS.beacon, 0);
  const material = state.config.buildCosts ? Object.fromEntries(Object.keys(stock).map(t => [t, Math.max(0, CONVOY.baseStock * CONVOY.material - issued(Number(t)))])) : undefined;
  return { ...state, convoys: { stock, calls: [], runs: [], ...(material ? { material } : {}) } };
}

const isTruck = (s: StructureState): boolean => cargoOnly(s) && drives(s);
const loaded = (s: StructureState): boolean => !!s.cargo && (s.cargo.bandages > 0 || s.cargo.magazines > 0 || (s.cargo.material ?? 0) > 0);
/** 69.30 step 6: the round types a FOB's crate keeps (MUNITIONS.fob's — never the salvo's light shells). */
type FobAmmo = keyof typeof MUNITIONS.fob;
const FOB_AMMO = Object.keys(MUNITIONS.fob) as FobAmmo[];
const bedHas = (s: StructureState): boolean => !!s.ammo && FOB_AMMO.some(t => (s.ammo![t] ?? 0) > 0);
/** Loaded for a run: its cargo, or (69.30 step 6, the munitions on) rounds in its bed. */
const carries = (state: Pick<GameState, 'config'>, s: StructureState): boolean => loaded(s) || (!!state.config.munitionsSupply && bedHas(s));
/** 69.30 step 6: the rounds `fob`'s crate can take of `bed`'s, by type (up to MUNITIONS.fob). */
const ammoTaken = (fob: StructureState, bed: StructureState['ammo']): Partial<Record<FobAmmo, number>> => {
  const out: Partial<Record<FobAmmo, number>> = {};
  for (const t of FOB_AMMO) { const n = Math.min(bed?.[t] ?? 0, Math.max(0, MUNITIONS.fob[t] - (fob.supply?.ammo?.[t] ?? 0))); if (n > 0) out[t] = n; }
  return out;
};
/** A full load aboard (what a truck takes on at home; its material too, with build costs — 69.4 step 6b). */
export const cargoFull = (c: StructureState['cargo'] | null | undefined): boolean => !!c && c.bandages >= CONVOY.load.bandages && c.magazines >= CONVOY.load.magazines && (c.material === undefined || c.material >= CONVOY.material);
/** Can `fob`'s crate (or, 6b, its store) take any of `cargo`? */
const takes = (fob: StructureState | undefined, cargo: StructureState['cargo'], bed?: StructureState['ammo']): boolean => !!fob && fob.health > 0 && !!fob.supply
  && ((!!cargo && ((cargo.bandages > 0 && fob.supply.bandages < FOB.stock.bandages) || (cargo.magazines > 0 && fob.supply.magazines < FOB.stock.magazines)
    || ((cargo.material ?? 0) > 0 && (fob.supply.material ?? 0) < BUILD_SUPPLY.fobCap)))
    || (bed !== undefined && Object.keys(ammoTaken(fob, bed)).length > 0));   // (69.30 step 6: or its rounds, the munitions on)
const peopleOf = (state: GameState) => PERSON_BODY_HEIGHT * personSetOf(state).k;
const low = (f: StructureState, material: boolean, ammo = false) => Math.min(f.supply!.bandages / FOB.stock.bandages, f.supply!.magazines / FOB.stock.magazines, material ? (f.supply!.material ?? 0) / BUILD_SUPPLY.fobCap : 1,
  ammo ? Math.min(...FOB_AMMO.map(t => (f.supply!.ammo?.[t] ?? 0) / MUNITIONS.fob[t])) : 1);   // (69.30 step 6: its rounds too, the munitions on)   // (6b: its store too, with build costs)

/** 69.30 step 6: `a` with `give` added (`sign` 1) or taken off (−1), by type. */
function addAmmo<T extends string>(a: Partial<Record<T, number>> | undefined, give: Partial<Record<T, number>>, sign: 1 | -1): Partial<Record<T, number>> {
  const out: Partial<Record<T, number>> = { ...(a ?? {}) };
  for (const t of Object.keys(give) as T[]) out[t] = (out[t] ?? 0) + sign * (give[t] ?? 0);
  return out;
}

/** The convoy leg in force for a unit, or null. */
export function convoyTaskFor(state: GameState, unitId: number, tick: number): ConvoyOrder | null {
  const task = squadTaskFor(state, unitId, tick);
  return task && task.order.kind === 'ride' && task.order.convoy ? task.order as ConvoyOrder : null;
}

/** `team`'s runs under way: truck id → its leg (either), from its living units' tasks in force. */
export function convoyBound(state: GameState, team: number, tick: number): Map<number, ConvoyOrder> {
  const out = new Map<number, ConvoyOrder>();
  if (!convoysOn(state.config)) return out;
  for (const p of state.players) {
    if (!p.alive || p.teamIndex !== team) continue;
    const o = convoyTaskFor(state, p.id, tick);
    if (o) out.set(o.structureId, o);
  }
  return out;
}

/** 69.42 (g): is `p` seated at `truck`'s wheel — its side's standing truck crew? */
const atWheel = (p: PlayerState, truck: StructureState | undefined): boolean => !!truck && p.mount?.bodyId === truck.id && truck.seats?.[p.mount.seat]?.role === 'driver';

/** May this unit be sent on a run: a living bot nobody holds, on foot (or 69.42 (g): at the run's truck's wheel), with no task and no human's order, not on a run? */
function freeForRun(state: GameState, p: PlayerState, tick: number, truck?: StructureState): boolean {
  const crew = atWheel(p, truck);
  if (!p.alive || !p.isBot || state.controllers?.[p.id] !== undefined || (p.mount && !crew) || p.airborne) return false;
  // (69.4's open note: the driver the respawn wave put by the truck for its call is free for the run though its squad has a
  // commander's task — it was spawned for the run, and the squad's tactic otherwise walked it off every time)
  const called = (state.convoys?.calls ?? []).some(c => c.closed === undefined && c.unitId === p.id);
  const task = squadTaskFor(state, p.id, tick);
  if ((task !== null && !((called || crew) && !task.units && task.by === 'commander')) || squadOrderSourceFor(state, p.id, tick) === 'human') return false;   // (69.42 (g): the crew at the wheel too: its squad's commander tasks are not its)
  return !(state.convoys?.runs ?? []).some(r => r.unitId === p.id && !r.end);
}

/** A truck that can set out: the side's, standing, loaded, nobody in it (69.42 (g): but its crew at the wheel), no run bound for it. */
const idleTruck = (state: GameState, t: StructureState, team: number, bound: ReadonlyMap<number, unknown>): boolean =>
  isTruck(t) && t.health > 0 && carries(state, t) && structureTeam(state, t) === team && !bound.has(t.id) && !(t.seats ?? []).some(x => x.occupant && x.role !== 'driver');

/** The FOBs of `team` running low with no run bound for them, the lowest first. */
function wanting(state: GameState, team: number, bound: ReadonlyMap<number, ConvoyOrder>): StructureState[] {
  const material = !!state.config.buildCosts;   // (6b: a FOB short of material wants a run too)
  const ammo = !!state.config.munitionsSupply;   // (69.30 step 6: and one short of rounds)
  const boundFobs = new Set([...bound.values()].filter(o => o.convoy.leg === 'out').map(o => o.convoy.fobId));
  return state.structures
    .filter(f => f.type === 'fob' && f.health > 0 && f.supply && structureTeam(state, f) === team && !boundFobs.has(f.id) && low(f, material, ammo) < CONVOY.callBelow)
    .sort((a, b) => low(a, material, ammo) - low(b, material, ammo) || a.id - b.id);
}

/** The nearest free bot of `team` within CONVOY.squadReach of `truck` (an id tie: the lower), or null. */
function driverFor(state: GameState, team: number, truck: StructureState, tick: number, taken: ReadonlySet<number> = new Set()): PlayerState | null {
  const planet = state.planets[truck.planetIndex];
  if (!planet) return null;
  const reach = CONVOY.squadReach * peopleOf(state);
  let best: PlayerState | null = null, bestD = Infinity;
  for (const p of state.players) {
    if (p.teamIndex !== team || p.planetIndex !== truck.planetIndex || taken.has(p.id) || !freeForRun(state, p, tick, truck)) continue;
    const d = arcDistance(p.surfaceDir, truck.surfaceDir, planet.radius);
    if (d <= reach && d < bestD) { best = p; bestD = d; }
  }
  return best;
}

/**
 * The commander's convoy errand for `team` this re-plan: the ride tasks to issue, each to one unit (`to: { units }`, a task
 * of CONVOY.legSeconds) — home legs for runs that have handed over what their FOB could take, then a run for each FOB
 * running low (the lowest first) with a loaded idle truck and a free bot near it.
 */
export function planConvoys(state: GameState, team: number, tick: number): Array<{ unitId: number; order: ConvoyOrder; expires: number }> {
  if (!convoysOn(state.config)) return [];
  const expires = tick + Math.round(CONVOY.legSeconds / PHYS_DT);
  const plans: Array<{ unitId: number; order: ConvoyOrder; expires: number }> = [];
  for (const p of state.players) {
    if (!p.alive || p.teamIndex !== team) continue;
    const o = convoyTaskFor(state, p.id, tick);
    if (!o || o.convoy.leg !== 'out' || !rideArrived(state, o)) continue;
    const t = state.structures.find(s => s.id === o.structureId);
    if (o.convoy.forward !== undefined) {   // 69.42 (e): the bed out by a vehicle — home once it wants nothing the bed holds
      if (!t || t.health <= 0 || !t.home) continue;
      const v = state.structures.find(s => s.id === o.convoy.forward);
      if (v && v.health > 0 && wantsFrom(state, v, t.ammo)) continue;
      plans.push({ unitId: p.id, order: { kind: 'ride', structureId: t.id, planetIndex: t.planetIndex, surfaceDir: t.home.surfaceDir, convoy: { fobId: -1, leg: 'back', forward: o.convoy.forward } }, expires });
      continue;
    }
    if (!t || t.health <= 0 || !t.home || takes(state.structures.find(s => s.id === o.convoy.fobId && structureTeam(state, s) === team), t.cargo, state.config.munitionsSupply ? t.ammo : undefined)) continue;
    plans.push({ unitId: p.id, order: { kind: 'ride', structureId: t.id, planetIndex: t.planetIndex, surfaceDir: t.home.surfaceDir, convoy: { fobId: o.convoy.fobId, leg: 'back' } }, expires });
  }
  const bound = convoyBound(state, team, tick);
  const usedTrucks = new Set(plans.map(p => p.order.structureId)), usedUnits = new Set(plans.map(p => p.unitId));
  for (const fob of wanting(state, team, bound)) {
    const planet = state.planets[fob.planetIndex];
    if (!planet) continue;
    // (74.5, 357: with the routes on and the world's roads, a truck goes only where its side's roads reach — a held ford cuts them)
    const roads = state.config.planetRoutes ? roadsFor(state, fob.planetIndex, team) : null;
    const reaches = (t: StructureState) => !roads?.world || Number.isFinite(routeCost(planet, t.surfaceDir, fob.surfaceDir, 'wheels', roads));
    const trucks = state.structures.filter(t => t.planetIndex === fob.planetIndex && !usedTrucks.has(t.id) && idleTruck(state, t, team, bound) && reaches(t))
      .sort((a, b) => arcDistance(a.surfaceDir, fob.surfaceDir, planet.radius) - arcDistance(b.surfaceDir, fob.surfaceDir, planet.radius) || a.id - b.id);
    for (const truck of trucks) {
      const driver = driverFor(state, team, truck, tick, usedUnits);
      if (!driver) continue;
      usedTrucks.add(truck.id); usedUnits.add(driver.id);
      plans.push({ unitId: driver.id, order: { kind: 'ride', structureId: truck.id, planetIndex: fob.planetIndex, surfaceDir: fob.surfaceDir, convoy: { fobId: fob.id, leg: 'out' } }, expires });
      break;
    }
  }
  // 69.42 (e): the bed brought forward — to the side's vehicle shortest of rounds and far from any dump of its side, one at a time
  {
    const person = peopleOf(state);
    for (const v of forwardWanted(state, team, bound)) {
      const planet = state.planets[v.planetIndex];
      const trucks = state.structures.filter(t => t.planetIndex === v.planetIndex && !usedTrucks.has(t.id) && idleTruck(state, t, team, bound) && !!t.home && wantsFrom(state, v, t.ammo))
        .sort((a, b) => arcDistance(a.surfaceDir, v.surfaceDir, planet.radius) - arcDistance(b.surfaceDir, v.surfaceDir, planet.radius) || a.id - b.id);
      const truck = trucks.find(t => driverFor(state, team, t, tick, usedUnits) !== null);
      if (!truck) continue;
      const driver = driverFor(state, team, truck, tick, usedUnits)!;
      const spot = stepToward(v.surfaceDir, truck.home!.surfaceDir, FORWARD_DUMP.backPeople * person, planet.radius);
      plans.push({ unitId: driver.id, order: { kind: 'ride', structureId: truck.id, planetIndex: v.planetIndex, surfaceDir: spot, convoy: { fobId: -1, leg: 'out', forward: v.id } }, expires });
      break;
    }
  }
  // 69.42 (h): a truck of the side away from home with no run — a run's end out there, a replacement parked at the pad or the
  // front FOB — driven home by a free bot near it (or its crew at the wheel): home it loads, and its driver holds the wheel
  // (away by the distance, not `atHome`: that is set on the 1 s beat, after the first plan)
  for (const t of state.structures) {
    if (!isTruck(t) || t.health <= 0 || !t.home || structureTeam(state, t) !== team || bound.has(t.id) || usedTrucks.has(t.id) || (t.seats ?? []).some(x => x.occupant && x.role !== 'driver')) continue;
    const planet = state.planets[t.planetIndex];
    if (!planet || arcDistance(t.home.surfaceDir, t.surfaceDir, planet.radius) <= CONVOY.loadReach * peopleOf(state)) continue;
    const driver = driverFor(state, team, t, tick, usedUnits);
    if (!driver) continue;
    usedTrucks.add(t.id); usedUnits.add(driver.id);
    plans.push({ unitId: driver.id, order: { kind: 'ride', structureId: t.id, planetIndex: t.planetIndex, surfaceDir: t.home.surfaceDir, convoy: { fobId: -1, leg: 'back' } }, expires });
  }
  return plans;
}

/**
 * 69.42 (e): the side's vehicles the bed should be brought out to, the shortest first — with the munitions on and no forward
 * run of the side under way: short of rounds (under a third), standing, with no dump of the side (a crate with rounds, a
 * parked bed) within FORWARD_DUMP.farPeople.
 */
function forwardWanted(state: GameState, team: number, bound: ReadonlyMap<number, ConvoyOrder>): StructureState[] {
  if (!state.config.munitionsSupply || [...bound.values()].some(o => o.convoy.forward !== undefined)) return [];
  const person = peopleOf(state), walk = personSetOf(state).walkSpeed;
  const far = (v: StructureState) => {
    const planet = state.planets[v.planetIndex];
    return !!planet && !state.structures.some(d => d.id !== v.id && d.planetIndex === v.planetIndex && ((dumpHolds(d) && crateTeam(state, d) === team) || (bedOf(d, walk) !== null && structureTeam(state, d) === team))
      && arcDistance(v.surfaceDir, d.surfaceDir, planet.radius) <= FORWARD_DUMP.farPeople * person);
  };
  return state.structures.filter(v => v.health > 0 && drives(v) && !cargoOnly(v) && structureTeam(state, v) === team && shareLeft(state, v) < 1 / 3 && far(v))
    .sort((a, b) => shareLeft(state, a) - shareLeft(state, b) || a.id - b.id);
}

/** 69.42 (e): `v`'s stores' share left (1 with none). */
function shareLeft(state: Pick<GameState, 'config'>, v: StructureState): number {
  let left = 0, cap = 0;
  for (const st of rearmStores(v, state.config)) { cap += st.cap; left += st.cap - st.short; }
  return cap > 0 ? left / cap : 1;
}

/** 69.42 (e): does `v` want a round `bed` holds (a store of its short of a type in the bed)? */
function wantsFrom(state: Pick<GameState, 'config'>, v: StructureState, bed: StructureState['ammo']): boolean {
  return !!bed && rearmStores(v, state.config).some(st => st.short > 0 && (bed[st.type] ?? 0) > 0);
}

/** 69.42 (e): `from` moved `d` (world units, along the ground) toward `to`, short of it (the point itself when nearer). */
function stepToward(from: Vec3, to: Vec3, d: number, radius: number): Vec3 {
  const a = vecNormalize(from), b = vecNormalize(to), arc = arcDistance(a, b, radius);
  if (arc <= d || arc <= 0) return { ...b };
  const t = d / arc;
  return vecNormalize({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t });
}

/**
 * 69.42 (g) (DECISIONS 268, the owner's "1a": a standing truck crew at the base): the wheel `team`'s next arrival takes —
 * its supply truck standing at home, loaded or not, with its driver's seat empty and no run bound for it; null when none
 * wants one. The respawn wave seats the side's next dead bot there (match.ts, ahead of 69.35's base crew), and it holds the
 * wheel at home (`truckCrewHolds`) till the commander sends it: the runs no longer wait on a wave's dead by the truck.
 */
export function truckCrewSeat(state: GameState, team: number, tick: number): { structureId: number; seat: number } | null {
  if (!convoysOn(state.config)) return null;
  const bound = convoyBound(state, team, tick);
  for (const t of state.structures) {
    if (!isTruck(t) || t.health <= 0 || !t.atHome || structureTeam(state, t) !== team || bound.has(t.id)) continue;
    const k = (t.seats ?? []).findIndex(x => x.role === 'driver');
    if (k >= 0 && !t.seats![k].occupant) return { structureId: t.id, seat: k };
  }
  return null;
}

/** 69.42 (g): does `p`, seated in `on`, hold its side's supply truck's wheel at home — the standing crew, waiting for its run? */
export function truckCrewHolds(state: GameState, p: PlayerState, on: StructureState): boolean {
  return convoysOn(state.config) && isTruck(on) && !!on.atHome && on.health > 0 && atWheel(p, on);
}

/** The truck a side's next respawn should spawn by (its open, unanswered call), or null. */
export function baseSpawnFor(state: GameState, team: number): { call: ConvoyCall; truck: StructureState } | null {
  const call = state.convoys?.calls.find(c => c.team === team && c.closed === undefined && c.unitId === undefined);
  const truck = call ? state.structures.find(s => s.id === call.truckId && s.health > 0) : undefined;
  return call && truck ? { call, truck } : null;
}

/** Record that the respawn wave put `unitId` by its side's called truck. */
export function noteBaseSpawn(state: GameState, team: number, unitId: number, tick: number): GameState {
  if (!state.convoys) return state;
  return { ...state, convoys: { ...state.convoys, calls: state.convoys.calls.map(c => c.team === team && c.closed === undefined && c.unitId === undefined ? { ...c, unitId, spawned: tick } : c) } };
}

/** The runs whose driver is home and not yet sent forward (the respawn wave redeploys them). */
export function redeploysDue(state: GameState): ConvoyRun[] {
  return (state.convoys?.runs ?? []).filter(r => r.home !== undefined && !r.end);
}

/** End a run (a redeploy forward, or staying at the base). */
export function endRun(state: GameState, unitId: number, tick: number, why: 'redeployed' | 'stayed', at?: 'fob' | 'relay' | 'beacon'): GameState {
  if (!state.convoys) return state;
  return { ...state, convoys: { ...state.convoys, runs: state.convoys.runs.map(r => r.unitId === unitId && !r.end ? { ...r, end: { tick, why, ...(at ? { at } : {}) } } : r) } };
}

/**
 * One beat of the convoys (the Match's, once a second): the base stocks grow; the trucks' cargo — lost with a wreck, handed
 * to the side's FOBs in reach, loaded at home from the base's stock; the runs' log follows the drivers' legs; and a side
 * whose run is wanted with no driver near the truck calls one. Null when nothing changed.
 */
export function stepConvoys(state: GameState, tick: number): { state: GameState; events: ConvoyEvent[] } | null {
  if (!convoysOn(state.config) || !state.convoys) return null;
  const events: ConvoyEvent[] = [];
  const person = peopleOf(state);
  let stock = state.convoys.stock;
  let material = state.convoys.material;   // 69.4 step 6b: the base's material (with buildCosts)
  let stocks: Pick<GameState, 'supplyStocks'> = state;   // DECISIONS 402: with finiteSupply a load's stores come out of the side's containers
  // the rear's output
  const every = Math.round(CONVOY.baseRefillS / PHYS_DT);
  if (tick > 0 && tick % every === 0) {
    const grown = Object.fromEntries(Object.entries(stock).map(([t, n]) => [t, Math.min(CONVOY.baseStock, n + 1)]));
    if (Object.keys(grown).some(t => grown[t] !== stock[Number(t)])) stock = grown;
    if (material) {   // 6b: the base's material grows by a load's, up to its stock's worth
      const more = Object.fromEntries(Object.entries(material).map(([t, n]) => [t, Math.min(CONVOY.baseStock * CONVOY.material, n + CONVOY.material)]));
      if (Object.keys(more).some(t => more[t] !== material![Number(t)])) material = more;
    }
  }
  // the cargo
  let structures: StructureState[] | null = null;
  const at = (i: number) => (structures ?? state.structures)[i];
  const set = (i: number, s: StructureState) => { (structures ??= state.structures.slice())[i] = s; };
  state.structures.forEach((s, i) => {
    if (!isTruck(s)) return;
    const team = structureTeam(state, s);
    if (team === null) return;
    if (s.health <= 0) {
      if (s.cargo || s.atHome) {
        const { cargo, atHome: _, ...rest } = s;
        set(i, rest);
        if (cargo && loaded(s)) events.push({ kind: 'convoyLost', structureId: s.id, team, bandages: cargo.bandages, magazines: cargo.magazines });
      }
      return;
    }
    const planet = state.planets[s.planetIndex];
    if (!planet) return;
    let cargo = s.cargo ? { ...s.cargo } : null;
    // 69.30 step 6: the bed's rounds ride along (the munitions on), handed over with the cargo
    let bed = state.config.munitionsSupply && bedHas(s) ? { ...s.ammo! } : null;
    if (bed && !(cargo && loaded(s))) {
      const fobs = state.structures
        .map((f, j) => ({ f, j, d: arcDistance(f.surfaceDir, s.surfaceDir, planet.radius) }))
        .filter(({ f, d }) => f.type === 'fob' && f.health > 0 && f.supply && f.planetIndex === s.planetIndex && d <= CONVOY.unloadReach * person && structureTeam(state, f) === team)
        .sort((a, b) => a.d - b.d || a.f.id - b.f.id);
      for (const { j } of fobs) {
        const f = at(j), give = ammoTaken(f, bed);
        if (!Object.keys(give).length) continue;
        set(j, { ...f, supply: { ...f.supply!, ammo: addAmmo(f.supply!.ammo, give, 1) } });
        bed = addAmmo(bed, give, -1);
        events.push({ kind: 'convoyDelivered', structureId: s.id, fobId: f.id, team, bandages: 0, magazines: 0, ammo: give });
      }
    }
    if (cargo && loaded(s)) {
      const fobs = state.structures
        .map((f, j) => ({ f, j, d: arcDistance(f.surfaceDir, s.surfaceDir, planet.radius) }))
        .filter(({ f, d }) => f.type === 'fob' && f.health > 0 && f.supply && f.planetIndex === s.planetIndex && d <= CONVOY.unloadReach * person && structureTeam(state, f) === team)
        .sort((a, b) => a.d - b.d || a.f.id - b.f.id);
      for (const { j } of fobs) {
        const f = at(j), supply = f.supply!;
        const bandages = Math.min(cargo.bandages, Math.max(0, FOB.stock.bandages - supply.bandages));
        const magazines = Math.min(cargo.magazines, Math.max(0, FOB.stock.magazines - supply.magazines));
        const mat = Math.min(cargo.material ?? 0, Math.max(0, BUILD_SUPPLY.fobCap - (supply.material ?? 0)));   // 6b: into its store, up to its cap
        const give = bed ? ammoTaken(f, bed) : {}, rounds = Object.keys(give).length > 0;   // (69.30 step 6: the bed's rounds with it)
        if (bandages <= 0 && magazines <= 0 && mat <= 0 && !rounds) continue;
        set(j, { ...f, supply: { ...supply, bandages: supply.bandages + bandages, magazines: supply.magazines + magazines, ...(mat > 0 ? { material: (supply.material ?? 0) + mat } : {}), ...(rounds ? { ammo: addAmmo(supply.ammo, give, 1) } : {}) } });
        cargo = { bandages: cargo.bandages - bandages, magazines: cargo.magazines - magazines, ...(cargo.material !== undefined ? { material: cargo.material - mat } : {}) };
        if (rounds) bed = addAmmo(bed!, give, -1);
        events.push({ kind: 'convoyDelivered', structureId: s.id, fobId: f.id, team, bandages, magazines, ...(mat > 0 ? { material: mat } : {}), ...(rounds ? { ammo: give } : {}) });
      }
    }
    const home = !!s.home && arcDistance(s.home.surfaceDir, s.surfaceDir, planet.radius) <= CONVOY.loadReach * person;
    if (home && !cargoFull(cargo) && (stock[team] ?? 0) > CONVOY.baseMinimum) {
      if (state.supplyStocks) {   // (DECISIONS 402: as far as the containers go; out, no load)
        const took = takeStock(stocks, team, CONVOY.load);
        stocks = took.state;
        cargo = { bandages: took.bandages, magazines: took.magazines };
      } else cargo = { ...CONVOY.load };
      stock = { ...stock, [team]: stock[team] - 1 };
      if (material) {   // 6b: and a load's material from the base's, as far as it goes
        const take = Math.min(CONVOY.material, material[team] ?? 0);
        cargo = { ...cargo, material: take };
        material = { ...material, [team]: (material[team] ?? 0) - take };
      }
      events.push({ kind: 'convoyLoaded', structureId: s.id, team });
    }
    if (cargo && cargo.bandages <= 0 && cargo.magazines <= 0 && (cargo.material ?? 0) <= 0) cargo = null;
    const was = s.cargo ?? null;
    const bedMoved = bed !== null && FOB_AMMO.some(t => (bed![t] ?? 0) !== (s.ammo?.[t] ?? 0));   // (69.30 step 6: rounds handed over)
    if (cargo?.bandages === was?.bandages && cargo?.magazines === was?.magazines && cargo?.material === was?.material && home === !!s.atHome && !bedMoved) return;
    const { cargo: _c, atHome: _h, ...rest } = at(i);
    set(i, { ...rest, ...(cargo ? { cargo } : {}), ...(home ? { atHome: true as const } : {}), ...(bedMoved ? { ammo: bed! } : {}) });
  });
  let next: GameState = structures ? { ...state, structures } : state;
  if (stocks !== state) next = { ...next, supplyStocks: stocks.supplyStocks };
  // the runs' log
  let runs = state.convoys.runs, calls = state.convoys.calls;
  const open = (unitId: number, truckId: number) => runs.some(r => r.unitId === unitId && r.truckId === truckId && !r.end);
  for (const p of next.players) {
    if (!p.alive) continue;
    const o = convoyTaskFor(next, p.id, tick);
    if (!o || o.convoy.leg !== 'out' || open(p.id, o.structureId)) continue;
    const called = calls.find(c => c.closed === undefined && c.unitId === p.id);
    runs = [...runs, { team: p.teamIndex, unitId: p.id, truckId: o.structureId, fobId: o.convoy.fobId, out: tick, ...(called ? { base: true as const } : {}) }];
    calls = calls.map(c => c.closed === undefined && (c.unitId === p.id || c.truckId === o.structureId) ? { ...c, closed: tick } : c);
  }
  const delivered = new Set(events.filter(e => e.kind === 'convoyDelivered').map(e => e.structureId));
  runs = runs.map(r => {
    if (r.end) return r;
    const u = next.players.find(p => p.id === r.unitId), t = next.structures.find(s => s.id === r.truckId);
    if (!t || t.health <= 0) return { ...r, end: { tick, why: 'lost' as const } };
    if (!u || !u.alive) return { ...r, end: { tick, why: 'died' as const } };
    let out = r;
    if (delivered.has(r.truckId) && r.delivered === undefined) out = { ...out, delivered: tick };
    if (out.home === undefined && !convoyTaskFor(next, r.unitId, tick)) {
      out = t.atHome && (out.delivered !== undefined || r.fobId === -1) ? { ...out, home: tick } : { ...out, end: { tick, why: 'expired' as const } };
      if (out.home !== undefined && truckCrewHolds(next, u, t)) out = { ...out, end: { tick, why: 'stayed' as const } };   // (69.42 (g): home at the wheel, it stays as the truck's crew, free for the next run)
    }
    return out;
  });
  // the calls: drop what is no longer wanted; call a driver where a run is wanted and none is near the truck
  for (const team of Object.keys(stock).map(Number)) {
    const bound = convoyBound(next, team, tick);
    const want = wanting(next, team, bound);
    calls = calls.map(c => {
      if (c.team !== team || c.closed !== undefined) return c;
      const t = next.structures.find(s => s.id === c.truckId);
      const answered = c.unitId !== undefined;
      const dead = answered && !next.players.find(p => p.id === c.unitId)?.alive;
      const wanted = c.fobId === -1 ? forwardWanted(next, team, bound).length > 0 : want.some(f => f.id === c.fobId);   // (69.42 (e): a call for the bed's forward run, while a vehicle wants it)
      const stale = !t || t.health <= 0 || !wanted || dead || (answered && tick - (c.spawned ?? c.tick) > Math.round(CONVOY.legSeconds / PHYS_DT));
      return stale ? { ...c, closed: tick } : c;
    });
    if (calls.some(c => c.team === team && c.closed === undefined)) continue;
    let called = false;
    for (const fob of want) {
      const truck = next.structures.find(t => t.atHome && t.planetIndex === fob.planetIndex && idleTruck(next, t, team, bound));
      if (!truck || driverFor(next, team, truck, tick)) continue;
      calls = [...calls, { team, truckId: truck.id, fobId: fob.id, tick }];
      called = true;
      break;
    }
    // 69.42 (e): the bed's forward run calls its driver from the base the same way (the side's people are at the front)
    if (!called) for (const v of forwardWanted(next, team, bound)) {
      const truck = next.structures.find(t => t.atHome && t.planetIndex === v.planetIndex && idleTruck(next, t, team, bound) && wantsFrom(next, v, t.ammo));
      if (!truck || driverFor(next, team, truck, tick)) continue;
      calls = [...calls, { team, truckId: truck.id, fobId: -1, tick }];
      break;
    }
  }
  const changed = stock !== state.convoys.stock || material !== state.convoys.material || runs !== state.convoys.runs || calls !== state.convoys.calls;
  if (!structures && !changed) return null;
  if (changed) next = { ...next, convoys: { stock, calls, runs, ...(material ? { material } : {}) } };
  return { state: next, events };
}
